/**
 * Pending Reviews Component
 * Displays projects eligible for review with quick review action
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Avatar,
  Chip,
  Divider,
  Alert,
  Grid,
  LinearProgress
} from '@mui/material';
import {
  Star,
  Schedule,
  CheckCircle,
  Assignment
} from '@mui/icons-material';
import projectService from '../../services/projectService';
import ReviewSubmissionForm from '../reviews/ReviewSubmissionForm';
import { formatCurrency, formatDate, formatRelativeTime } from '../../utils/helpers';

const PendingReviews = ({ userId, onReviewSubmitted }) => {
  const { user } = useAuth();
  const [pendingProjects, setPendingProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewDialog, setReviewDialog] = useState({ 
    open: false, 
    project: null, 
    contractor: null 
  });

  useEffect(() => {
    loadPendingReviews();
  }, [userId]);

  const loadPendingReviews = async () => {
    try {
      setLoading(true);
      const projects = await projectService.getProjectsEligibleForReview(userId);
      setPendingProjects(projects);
    } catch (error) {
      console.error('Error loading pending reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenReviewDialog = (project) => {
    // Contractor info comes from the project JOIN (contractor_id, contractor_name fields)
    const contractor = project.contractor_id
      ? {
          id: project.contractor_id,
          first_name: project.contractor_first_name || project.contractor_name?.split(' ')[0] || '',
          last_name: project.contractor_last_name || project.contractor_name?.split(' ').slice(1).join(' ') || '',
          company_name: project.contractor_company || ''
        }
      : null;

    setReviewDialog({
      open: true,
      project,
      contractor
    });
  };

  const handleCloseReviewDialog = () => {
    setReviewDialog({ open: false, project: null, contractor: null });
  };

  const handleReviewSubmitted = async (reviewData) => {
    try {
      // Mark project as reviewed
      await projectService.markProjectReviewed(reviewDialog.project.id);
      
      // Reload pending reviews
      await loadPendingReviews();
      
      // Close dialog
      handleCloseReviewDialog();
      
      // Notify parent component
      if (onReviewSubmitted) {
        onReviewSubmitted(reviewData);
      }
    } catch (error) {
      console.error('Error marking project as reviewed:', error);
    }
  };

  const handleDismissReview = async (projectId) => {
    try {
      await projectService.markProjectReviewed(projectId);
      await loadPendingReviews();
    } catch (error) {
      console.error('Error dismissing review:', error);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Star color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Pending Reviews
            </Typography>
          </Box>
          <LinearProgress />
          <Typography variant="body2" sx={{ textAlign: 'center', mt: 2 }}>
            Loading pending reviews...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (pendingProjects.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Star color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Pending Reviews
            </Typography>
          </Box>
          <Alert severity="info">
            <Typography variant="body2">
              No pending reviews. Complete more projects to help other clients by sharing your contractor experiences!
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const clientData = {
    id: userId || user?.id,
    first_name: user?.first_name || user?.firstName || '',
    last_name: user?.last_name || user?.lastName || ''
  };

  return (
    <>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Star color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Pending Reviews
              </Typography>
              <Chip 
                label={pendingProjects.length} 
                size="small" 
                color="primary" 
                sx={{ fontWeight: 600 }}
              />
            </Box>
          </Box>

          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Help the Eqaya community!
            </Typography>
            <Typography variant="body2">
              You have {pendingProjects.length} completed project{pendingProjects.length !== 1 ? 's' : ''} waiting for your review. 
              Your feedback helps other clients make informed decisions.
            </Typography>
          </Alert>

          <Grid container spacing={2}>
            {pendingProjects.map((project) => (
              <Grid item xs={12} key={project.id}>
                <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                          {project.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          {project.description}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Assignment sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {project.location}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                            <Typography variant="caption" color="text.secondary">
                              Completed {formatRelativeTime(project.actualCompletion)}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                            {formatCurrency(project.budget)}
                          </Typography>
                          <Chip 
                            label="Completed" 
                            size="small" 
                            color="success" 
                            icon={<CheckCircle />}
                          />
                        </Box>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        onClick={() => handleDismissReview(project.id)}
                        color="inherit"
                      >
                        Skip
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Star />}
                        onClick={() => handleOpenReviewDialog(project)}
                      >
                        Write Review
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Review Submission Dialog */}
      {reviewDialog.open && (
        <ReviewSubmissionForm
          open={reviewDialog.open}
          onClose={handleCloseReviewDialog}
          contractorData={reviewDialog.contractor}
          projectData={reviewDialog.project}
          clientData={clientData}
          onReviewSubmitted={handleReviewSubmitted}
        />
      )}
    </>
  );
};

export default PendingReviews;